import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Database,
  Car,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Activity,
  BarChart3,
  RotateCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTenants } from '@/hooks/useTenants';
import { useExecutionHistory, useRunDataCollectionTask } from '@/hooks/useDataCollection';
import { useVINJobsHistory, useCreateVINJob } from '@/hooks/useVINProcessing';
import { cn, utcToESTDateString } from '@/lib/utils';
import {
  normalizeVINProcessingExecution,
  normalizeDataCollectionExecution
} from '@/types/executionHistory';
import {
  normalizeStatus,
  isCompletedStatus,
  isFailedStatus,
  isRunningStatus
} from '@/utils/executionStatus';

type ExecutionType = 'all' | 'data_collection' | 'vin_processing';
type StatusFilter = 'all' | 'running' | 'completed' | 'failed';

export const ExecutionHistoryTab: React.FC = () => {
  const { toast } = useToast();

  // Filter state
  const [selectedTenant, setSelectedTenant] = useState<string>('__all__');
  const [typeFilter, setTypeFilter] = useState<ExecutionType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Auto-expand today's section by default
  const todayDateKey = utcToESTDateString(new Date().toISOString());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set([todayDateKey]));
  const [visibleExecutions, setVisibleExecutions] = useState(50);

  // Re-run state
  const [rerunningExecutionId, setRerunningExecutionId] = useState<string | null>(null);
  const [rerunConfirmDialogOpen, setRerunConfirmDialogOpen] = useState(false);
  const [executionToRerun, setExecutionToRerun] = useState<any | null>(null);

  // Data fetching - match WorkflowManagementTab pattern
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const {
    data: dataCollectionHistory,
    isLoading: dcLoading,
    isFetching: dcFetching,
    error: dcError,
    refetch: refetchDC
  } = useExecutionHistory(100);
  const {
    data: vinProcessingHistory,
    isLoading: vpLoading,
    isFetching: vpFetching,
    error: vpError,
    refetch: refetchVP
  } = useVINJobsHistory({ limit: 5 }); // Match WorkflowManagementTab for instant loading

  // Mutations for re-running
  const runTaskMutation = useRunDataCollectionTask();
  const createVINJobMutation = useCreateVINJob();

  // Only block on data collection - VIN processing can load in background
  const isInitialLoading = dcLoading;
  const hasError = dcError;

  // Create unified execution history matching WorkflowManagementTab pattern
  const unifiedExecutionHistory = useMemo(() => {
    const unified: any[] = [];

    // Add data collection executions - use normalization function
    if (dataCollectionHistory && Array.isArray(dataCollectionHistory)) {
      dataCollectionHistory.forEach(exec => {
        const normalized = normalizeDataCollectionExecution(exec);
        unified.push({
          ...normalized,
          executionType: 'data_collection',
          run_id: normalized.run_id || normalized.id,
          task_name: normalized.task_name,
          workflow_name: normalized.workflow_name
        });
      });
    }

    // Add VIN processing executions - use normalization function
    if (vinProcessingHistory?.executions && vinProcessingHistory.executions.length > 0) {
      vinProcessingHistory.executions.forEach(exec => {
        // Debug log to see actual data structure
        console.log('[ExecutionHistoryTab] VIN Processing raw exec:', {
          job_id: exec.job_id,
          entry_point: exec.entry_point,
          input_params: exec.input_params,
          oem_fields: {
            make_name: exec.input_params?.make_name,
            makeName: exec.input_params?.makeName,
            oem: exec.input_params?.oem,
            make: exec.input_params?.make
          },
          zone_fields: {
            zone_slug: exec.input_params?.zone_slug,
            zoneSlug: exec.input_params?.zoneSlug,
            zone: exec.input_params?.zone
          }
        });
        const normalized = normalizeVINProcessingExecution(exec);
        unified.push({
          ...normalized,
          executionType: 'vin_processing',
          run_id: normalized.job_id || normalized.id,
          task_name:
            normalized.entry_point === 'inventory_date_make'
              ? 'Process VINs from Inventory'
              : normalized.entry_point === 'step1_execution'
                ? 'Process VINs from Data Collection'
                : normalized.entry_point === 'apify_run_id'
                  ? 'Process VINs from Apify Run'
                  : 'VIN Processing',
          workflow_name: 'Step 2: Process VINs'
        });
      });
    }

    // Sort by started_at descending (most recent first)
    unified.sort((a, b) => {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime;
    });

    return unified;
  }, [dataCollectionHistory, vinProcessingHistory]);

  // Apply filters
  const filteredExecutions = useMemo(() => {
    return unifiedExecutionHistory.filter(exec => {
      // Type filter
      if (typeFilter !== 'all' && exec.executionType !== typeFilter) return false;

      // Status filter
      if (statusFilter !== 'all') {
        const status = normalizeStatus(exec.status);
        if (statusFilter === 'running' && !isRunningStatus(status)) return false;
        if (statusFilter === 'completed' && !isCompletedStatus(status)) return false;
        if (statusFilter === 'failed' && !isFailedStatus(status)) return false;
      }

      // Tenant filter
      if (selectedTenant !== '__all__' && exec.tenant_id !== selectedTenant) return false;

      return true;
    });
  }, [unifiedExecutionHistory, typeFilter, statusFilter, selectedTenant]);

  // Group executions by date - matching WorkflowManagementTab pattern
  const groupedExecutionHistory = useMemo(() => {
    if (!filteredExecutions || filteredExecutions.length === 0) return [];

    const limitedExecutions = filteredExecutions.slice(0, visibleExecutions);
    const groups = new Map<string, any[]>();

    limitedExecutions.forEach(exec => {
      const date = exec.started_at ? utcToESTDateString(exec.started_at) : 'Unknown';
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      const group = groups.get(date);
      if (group) {
        group.push(exec);
      }
    });

    return Array.from(groups.entries())
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
      .map(([date, executions]) => ({
        date,
        executions: [...executions].sort((a, b) => {
          const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
          const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
          return bTime - aTime;
        })
      }));
  }, [filteredExecutions, visibleExecutions]);

  // Calculate serial numbers
  const executionSerialMap = useMemo(() => {
    const serialMap = new Map<string, number>();
    let serialNumber = 1;
    groupedExecutionHistory.forEach(dateGroup => {
      dateGroup.executions.forEach(exec => {
        const execId = exec.id || exec.run_id || exec.job_id || exec.started_at || '';
        if (execId) {
          serialMap.set(execId, serialNumber);
          serialNumber++;
        }
      });
    });
    return serialMap;
  }, [groupedExecutionHistory]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredExecutions.length;
    const running = filteredExecutions.filter(e =>
      isRunningStatus(normalizeStatus(e.status))
    ).length;
    const completed = filteredExecutions.filter(e =>
      isCompletedStatus(normalizeStatus(e.status))
    ).length;
    const failed = filteredExecutions.filter(e => isFailedStatus(normalizeStatus(e.status))).length;
    const dcCount = filteredExecutions.filter(e => e.executionType === 'data_collection').length;
    const vpCount = filteredExecutions.filter(e => e.executionType === 'vin_processing').length;

    return { total, running, completed, failed, dcCount, vpCount };
  }, [filteredExecutions]);

  // Refresh all data
  const handleRefresh = () => {
    refetchDC();
    refetchVP();
  };

  // Handle re-running an execution
  const handleRerunExecution = async () => {
    if (!executionToRerun) return;

    const exec = executionToRerun;
    const execId = exec.id || exec.run_id || exec.job_id || '';
    setRerunningExecutionId(execId);
    setRerunConfirmDialogOpen(false);

    try {
      if (exec.executionType === 'data_collection') {
        // Re-run Step 1: Data Collection
        if (!exec.task_name) {
          toast({
            title: 'Cannot Re-run',
            description: 'Cannot re-run: task name is missing.',
            variant: 'destructive'
          });
          setRerunningExecutionId(null);
          return;
        }

        toast({
          title: 'Re-running Data Collection',
          description: `Starting task "${exec.task_name}"...`
        });

        const response = await runTaskMutation.mutateAsync({
          task_name: exec.task_name
        });

        toast({
          title: 'Data Collection Started',
          description: `Task "${exec.task_name}" has been re-run. Run ID: ${response.run_id?.slice(0, 8)}...`
        });

        // Refresh execution history
        refetchDC();
      } else {
        // Re-run Step 2: VIN Processing
        const inputParams = exec.input_params || {};

        if (!inputParams.tenant_id && !exec.tenant_id) {
          toast({
            title: 'Cannot Re-run',
            description: 'Cannot re-run: tenant ID is missing from the execution.',
            variant: 'destructive'
          });
          setRerunningExecutionId(null);
          return;
        }

        toast({
          title: 'Re-running VIN Processing',
          description: 'Starting VIN processing job...'
        });

        // Build the request based on entry point
        const entryPoint = exec.entry_point || 'inventory_date_make';
        const tenantId = inputParams.tenant_id || exec.tenant_id;

        if (entryPoint === 'inventory_date_make') {
          await createVINJobMutation.mutateAsync({
            tenant_id: tenantId,
            entry_point: 'inventory_date_make',
            make_name: inputParams.make_name,
            inventory_date: inputParams.inventory_date,
            zone_slug: inputParams.zone_slug
          });
        } else if (entryPoint === 'step1_execution') {
          await createVINJobMutation.mutateAsync({
            tenant_id: tenantId,
            entry_point: 'step1_execution',
            step1_execution_id: inputParams.step1_execution_id
          });
        } else if (entryPoint === 'apify_run_id') {
          await createVINJobMutation.mutateAsync({
            tenant_id: tenantId,
            entry_point: 'apify_run_id',
            apify_run_id: inputParams.apify_run_id
          });
        }

        toast({
          title: 'VIN Processing Started',
          description:
            'VIN processing job has been started. It will appear in the execution history.'
        });

        // Refresh VIN processing history
        refetchVP();
      }
    } catch (error) {
      let errorDescription = 'An unknown error occurred';
      if (error instanceof Error) {
        errorDescription = error.message;
      }

      toast({
        title: 'Failed to re-run execution',
        description: errorDescription,
        variant: 'destructive'
      });
    } finally {
      setRerunningExecutionId(null);
      setExecutionToRerun(null);
    }
  };

  // Format date for section headers
  const formatDateHeader = (dateStr: string): string => {
    if (dateStr === 'Unknown') return 'Unknown Date';
    const todayEST = utcToESTDateString(new Date().toISOString());
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayEST = utcToESTDateString(yesterdayDate.toISOString());

    if (dateStr === todayEST) return 'Today';
    if (dateStr === yesterdayEST) return 'Yesterday';

    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format duration
  const formatDuration = (totalSeconds: number | undefined | null): string => {
    if (totalSeconds === undefined || totalSeconds === null) return 'N/A';
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.floor(totalSeconds % 60);
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${totalSeconds}s`;
  };

  // Get workflow step info
  const getWorkflowStep = (exec: any): { step: number; label: string } => {
    if (exec.executionType === 'vin_processing') {
      return { step: 2, label: 'Step 2: Process VINs' };
    }
    const taskName = exec.task_name;
    if (!taskName) return { step: 0, label: 'Unknown' };
    if (taskName.toLowerCase().includes('cargurus')) {
      return { step: 1, label: 'Step 1: Refresh Car Inventory' };
    }
    return { step: 2, label: 'Step 2: Process VINs' };
  };

  // Calculate success rate for color coding
  const calculateSuccessRate = (exec: any): number | null => {
    if (!isCompletedStatus(normalizeStatus(exec.status))) return null;
    if (exec.executionType === 'vin_processing') {
      if (exec.total_vins && exec.total_vins > 0) {
        return Math.round(((exec.processed_vins || 0) / exec.total_vins) * 100);
      }
      if (exec.processed_vins && exec.processed_vins > 0) return 100;
      return null;
    }
    if (exec.records_processed && exec.records_processed > 0) return 100;
    return null;
  };

  const getSuccessColorClass = (successRate: number | null): string => {
    if (successRate === null) return '';
    if (successRate >= 90)
      return 'bg-green-50/50 dark:bg-green-950/20 border-l-4 border-l-green-500';
    if (successRate >= 70)
      return 'bg-yellow-50/50 dark:bg-yellow-950/20 border-l-4 border-l-yellow-500';
    return 'bg-red-50/50 dark:bg-red-950/20 border-l-4 border-l-red-500';
  };

  // Build metadata for popover
  const buildMetadata = (exec: any) => {
    const metadata: Record<string, unknown> = {};

    if (exec.run_id) metadata['Run ID'] = exec.run_id;
    if (exec.job_id) metadata['Job ID'] = exec.job_id;
    if (exec.gcs_path) metadata['GCS Path'] = exec.gcs_path;
    if (exec.errors) metadata['Errors'] = exec.errors;

    if (exec.executionType === 'data_collection') {
      if (exec.validation_status) metadata['Validation Status'] = exec.validation_status;
      if (exec.validation_summary) metadata['Validation Summary'] = exec.validation_summary;
    }

    if (exec.input_params && Object.keys(exec.input_params).length > 0) {
      metadata['Input Parameters'] = exec.input_params;
    }
    if (exec.inventory_summary) metadata['Inventory Summary'] = exec.inventory_summary;

    if (exec.executionType === 'vin_processing') {
      if (exec.total_recalls !== undefined && exec.total_recalls !== null) {
        metadata['Total Recalls Found'] = exec.total_recalls.toLocaleString();
      }
      if (
        exec.recalls_with_advance_notice !== undefined &&
        exec.recalls_with_advance_notice !== null
      ) {
        metadata['Recalls with Advance Notice'] = exec.recalls_with_advance_notice.toLocaleString();
      }
      if (exec.processed_vins_list && exec.processed_vins_list.length > 0) {
        metadata['Processed VINs List'] = exec.processed_vins_list;
      } else if (exec.processed_vins_list_gcs) {
        metadata['Processed VINs List (GCS Path)'] = exec.processed_vins_list_gcs;
      }
      if (exec.failed_vins_list && exec.failed_vins_list.length > 0) {
        metadata['Failed VINs List'] = exec.failed_vins_list;
      } else if (exec.failed_vins_list_gcs) {
        metadata['Failed VINs List (GCS Path)'] = exec.failed_vins_list_gcs;
      }
    }

    if (exec.output_summary && Object.keys(exec.output_summary).length > 0) {
      metadata['Output Summary'] = exec.output_summary;
    }
    if (exec.entry_point) metadata['Entry Point'] = exec.entry_point;
    if (exec.zone) metadata['Zone'] = exec.zone;
    if (exec.tenant_id) metadata['Tenant ID'] = exec.tenant_id;
    if (exec.requested_by) metadata['Requested By'] = exec.requested_by;

    return metadata;
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='p-3 bg-primary/10 rounded-lg'>
            <Activity className='h-6 w-6 text-primary' />
          </div>
          <div>
            <h2 className='text-2xl font-bold'>Execution History</h2>
            <p className='text-muted-foreground'>
              Monitor all workflow executions across data collection and VIN processing
            </p>
          </div>
        </div>
        <Button variant='outline' onClick={handleRefresh} disabled={dcFetching || vpFetching}>
          <RefreshCw className={cn('h-4 w-4 mr-2', (dcFetching || vpFetching) && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              <BarChart3 className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm text-muted-foreground'>Total</span>
            </div>
            <p className='text-2xl font-bold'>{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              <Loader2 className='h-4 w-4 text-blue-500 animate-spin' />
              <span className='text-sm text-muted-foreground'>Running</span>
            </div>
            <p className='text-2xl font-bold text-blue-600'>{stats.running}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              <CheckCircle className='h-4 w-4 text-green-500' />
              <span className='text-sm text-muted-foreground'>Completed</span>
            </div>
            <p className='text-2xl font-bold text-green-600'>{stats.completed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              <XCircle className='h-4 w-4 text-red-500' />
              <span className='text-sm text-muted-foreground'>Failed</span>
            </div>
            <p className='text-2xl font-bold text-red-600'>{stats.failed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              <Database className='h-4 w-4 text-blue-500' />
              <span className='text-sm text-muted-foreground'>Data Collection</span>
            </div>
            <p className='text-2xl font-bold'>{stats.dcCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              {vpLoading ? (
                <Loader2 className='h-4 w-4 text-purple-500 animate-spin' />
              ) : (
                <Car className='h-4 w-4 text-purple-500' />
              )}
              <span className='text-sm text-muted-foreground'>VIN Processing</span>
            </div>
            <p className='text-2xl font-bold'>
              {vpLoading ? (
                <span className='text-muted-foreground text-sm'>Loading...</span>
              ) : (
                stats.vpCount
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex flex-wrap items-center gap-4'>
            <div className='flex items-center gap-2'>
              <Label className='text-sm whitespace-nowrap'>Tenant:</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className='w-[200px] h-8'>
                  <SelectValue placeholder='All tenants' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='__all__'>All tenants</SelectItem>
                  {tenantsLoading ? (
                    <SelectItem value='__loading__' disabled>
                      Loading...
                    </SelectItem>
                  ) : (
                    tenants?.map(tenant => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-center gap-2'>
              <Label className='text-sm whitespace-nowrap'>Type:</Label>
              <Select value={typeFilter} onValueChange={(v: ExecutionType) => setTypeFilter(v)}>
                <SelectTrigger className='w-[180px] h-8'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Types</SelectItem>
                  <SelectItem value='data_collection'>Data Collection</SelectItem>
                  <SelectItem value='vin_processing'>VIN Processing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-center gap-2'>
              <Label className='text-sm whitespace-nowrap'>Status:</Label>
              <Select value={statusFilter} onValueChange={(v: StatusFilter) => setStatusFilter(v)}>
                <SelectTrigger className='w-[150px] h-8'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Statuses</SelectItem>
                  <SelectItem value='running'>Running</SelectItem>
                  <SelectItem value='completed'>Completed</SelectItem>
                  <SelectItem value='failed'>Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Execution History */}
      {isInitialLoading ? (
        <div className='flex items-center justify-center h-64'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
        </div>
      ) : hasError ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12 gap-4'>
            <AlertCircle className='h-6 w-6 text-destructive' />
            <div className='text-destructive font-medium'>Error Loading Execution History</div>
            <div className='text-sm text-muted-foreground'>
              {(dcError || vpError) instanceof Error
                ? (dcError || vpError)?.message
                : 'Failed to load execution history. Please try again.'}
            </div>
            <Button variant='outline' size='sm' onClick={handleRefresh}>
              <RefreshCw className='h-4 w-4 mr-2' />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : groupedExecutionHistory.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12 gap-4'>
            <Clock className='h-12 w-12 text-muted-foreground' />
            <div className='text-center'>
              <h3 className='text-lg font-semibold'>No Executions Found</h3>
              <p className='text-sm text-muted-foreground'>
                {typeFilter !== 'all' || statusFilter !== 'all' || selectedTenant !== '__all__'
                  ? 'Try adjusting your filters'
                  : 'No workflow executions have been recorded yet'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className='space-y-6'>
          {groupedExecutionHistory.map(dateGroup => {
            const sectionKey = dateGroup.date;
            const isExpanded = expandedSections.has(sectionKey);

            return (
              <div
                key={dateGroup.date}
                className='border rounded-lg bg-card overflow-hidden shadow-sm'
              >
                <Collapsible
                  open={isExpanded}
                  onOpenChange={open => {
                    setExpandedSections(prev => {
                      const updated = new Set(prev);
                      if (open) {
                        updated.add(sectionKey);
                      } else {
                        updated.delete(sectionKey);
                      }
                      return updated;
                    });
                  }}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant='ghost'
                      className='w-full justify-between px-4 py-3 h-auto hover:bg-muted/50 border-b'
                    >
                      <div className='flex items-center gap-2'>
                        {isExpanded ? (
                          <ChevronDown className='h-4 w-4 text-muted-foreground' />
                        ) : (
                          <ChevronRight className='h-4 w-4 text-muted-foreground' />
                        )}
                        <h4 className='text-sm font-semibold text-foreground'>
                          {formatDateHeader(dateGroup.date)}
                        </h4>
                        <Badge variant='outline' className='text-xs'>
                          {dateGroup.executions.length} execution
                          {dateGroup.executions.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Table>
                      <TableHeader>
                        <TableRow className='bg-muted/20'>
                          <TableHead className='w-[60px]'>#</TableHead>
                          <TableHead>Workflow Step</TableHead>
                          <TableHead>OEM</TableHead>
                          <TableHead>Zone</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Started</TableHead>
                          <TableHead>Finished</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead className='text-right'>Records/VINs</TableHead>
                          <TableHead>Metadata</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dateGroup.executions.map((exec, index) => {
                          const workflowStep = getWorkflowStep(exec);
                          const successRate = calculateSuccessRate(exec);
                          const successColorClass = getSuccessColorClass(successRate);
                          const metadata = buildMetadata(exec);
                          const hasMetadata = Object.keys(metadata).length > 0;
                          const execId =
                            exec.id || exec.run_id || exec.job_id || exec.started_at || '';
                          const serialNumber = execId ? executionSerialMap.get(execId) : null;

                          // Status badge
                          let statusBadge;
                          if (isCompletedStatus(normalizeStatus(exec.status))) {
                            statusBadge = (
                              <Badge className='bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'>
                                <CheckCircle className='h-3 w-3 mr-1' />
                                Completed
                              </Badge>
                            );
                          } else if (isFailedStatus(normalizeStatus(exec.status))) {
                            statusBadge = (
                              <Badge className='bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30'>
                                <AlertCircle className='h-3 w-3 mr-1' />
                                Failed
                              </Badge>
                            );
                          } else if (isRunningStatus(normalizeStatus(exec.status))) {
                            statusBadge = (
                              <div className='flex flex-col gap-1'>
                                <Badge className='bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30 animate-pulse'>
                                  <RefreshCw className='h-3 w-3 mr-1 animate-spin' />
                                  Running
                                </Badge>
                                {exec.executionType === 'data_collection' && (
                                  <span className='text-xs text-muted-foreground'>
                                    Apify task running...
                                  </span>
                                )}
                              </div>
                            );
                          } else {
                            statusBadge = (
                              <Badge variant='outline' className='capitalize'>
                                {exec.status || 'Unknown'}
                              </Badge>
                            );
                          }

                          return (
                            <TableRow
                              key={`${dateGroup.date}-${index}-${execId}`}
                              className={successColorClass}
                            >
                              <TableCell className='text-center font-mono text-sm text-muted-foreground'>
                                {serialNumber || '-'}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={workflowStep.step === 1 ? 'default' : 'secondary'}
                                  className={
                                    workflowStep.step === 1
                                      ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30'
                                      : 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30'
                                  }
                                >
                                  {workflowStep.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {exec.executionType === 'vin_processing' ? (
                                  <span className='text-sm text-muted-foreground'>
                                    {(() => {
                                      // Try multiple sources for OEM/make info with various field names
                                      const params = exec.input_params || {};
                                      const makeName =
                                        params.make_name ||
                                        params.makeName ||
                                        params.oem ||
                                        params.OEM ||
                                        params.make ||
                                        params.Make ||
                                        exec.oem;
                                      if (makeName) {
                                        return (
                                          String(makeName).charAt(0).toUpperCase() +
                                          String(makeName).slice(1)
                                        );
                                      }
                                      // Show entry point info when OEM not available
                                      if (exec.entry_point === 'step1_execution') {
                                        return <span className='text-xs italic'>from Step 1</span>;
                                      }
                                      if (exec.entry_point === 'apify_run_id') {
                                        return <span className='text-xs italic'>from Apify</span>;
                                      }
                                      return '-';
                                    })()}
                                  </span>
                                ) : (
                                  <span className='text-sm font-medium'>
                                    {exec.oem
                                      ? exec.oem.charAt(0).toUpperCase() + exec.oem.slice(1)
                                      : 'Unknown'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className='text-sm text-muted-foreground'>
                                  {(() => {
                                    // Try multiple sources for zone info with various field names
                                    const params = exec.input_params || {};
                                    const zone =
                                      params.zone_slug ||
                                      params.zoneSlug ||
                                      params.zone ||
                                      params.Zone ||
                                      exec.zone;
                                    if (zone) {
                                      return String(zone);
                                    }
                                    // Show entry point info when zone not available
                                    if (
                                      exec.entry_point === 'step1_execution' ||
                                      exec.entry_point === 'apify_run_id'
                                    ) {
                                      return <span className='text-xs italic'>see metadata</span>;
                                    }
                                    return '-';
                                  })()}
                                </span>
                              </TableCell>
                              <TableCell>{statusBadge}</TableCell>
                              <TableCell className='text-sm text-muted-foreground'>
                                {exec.started_at
                                  ? new Date(exec.started_at).toLocaleString()
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className='text-sm text-muted-foreground'>
                                {exec.finished_at
                                  ? new Date(exec.finished_at).toLocaleString()
                                  : isRunningStatus(normalizeStatus(exec.status))
                                    ? 'Running...'
                                    : '-'}
                              </TableCell>
                              <TableCell className='text-sm font-mono'>
                                {isRunningStatus(normalizeStatus(exec.status))
                                  ? 'Running'
                                  : formatDuration(exec.duration_seconds)}
                              </TableCell>
                              <TableCell className='text-right font-mono text-sm'>
                                {exec.executionType === 'vin_processing'
                                  ? exec.processed_vins !== undefined &&
                                    exec.processed_vins !== null
                                    ? `${exec.processed_vins.toLocaleString()} / ${exec.total_vins?.toLocaleString() || '?'}`
                                    : '-'
                                  : exec.records_processed?.toLocaleString() ||
                                    exec.records?.toLocaleString() ||
                                    '-'}
                              </TableCell>
                              <TableCell>
                                {hasMetadata ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        size='sm'
                                        className='h-8 text-xs'
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <Database className='h-3 w-3 mr-1' />
                                        View
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className='w-[500px] max-h-[600px] overflow-y-auto'
                                      align='start'
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <div className='space-y-4'>
                                        <div className='space-y-1'>
                                          <div className='font-semibold text-sm flex items-center gap-2'>
                                            <Database className='h-4 w-4' />
                                            Execution Metadata
                                          </div>
                                          <div className='text-xs text-muted-foreground'>
                                            All metadata for investigation
                                          </div>
                                        </div>
                                        <div className='space-y-3'>
                                          {Object.entries(metadata)
                                            .filter(
                                              ([, value]) => value !== null && value !== undefined
                                            )
                                            .map(([key, value]) => {
                                              let formattedValue: string;
                                              const isStringValue = typeof value === 'string';

                                              if (
                                                typeof value === 'object' &&
                                                value !== null &&
                                                !Array.isArray(value)
                                              ) {
                                                formattedValue = JSON.stringify(value, null, 2);
                                              } else if (Array.isArray(value)) {
                                                formattedValue = JSON.stringify(value, null, 2);
                                              } else if (typeof value === 'boolean') {
                                                formattedValue = value ? 'Yes' : 'No';
                                              } else {
                                                formattedValue = String(value);
                                              }
                                              return (
                                                <div
                                                  key={key}
                                                  className='border-b border-border/50 pb-3 last:border-0 last:pb-0'
                                                >
                                                  <div className='font-medium text-sm text-foreground mb-1.5'>
                                                    {key}
                                                  </div>
                                                  <div
                                                    className={`break-words whitespace-pre-wrap ${
                                                      isStringValue &&
                                                      (value as string).length > 100
                                                        ? 'font-mono text-[11px]'
                                                        : 'text-xs'
                                                    } text-muted-foreground bg-muted/50 p-2 rounded max-h-[200px] overflow-y-auto`}
                                                  >
                                                    {formattedValue}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className='text-sm text-muted-foreground'>-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const isRerunning = rerunningExecutionId === execId;
                                  return (
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      className='h-8 w-8 p-0'
                                      onClick={e => {
                                        e.stopPropagation();
                                        setExecutionToRerun(exec);
                                        setRerunConfirmDialogOpen(true);
                                      }}
                                      disabled={
                                        isRerunning ||
                                        runTaskMutation.isPending ||
                                        createVINJobMutation.isPending
                                      }
                                      title='Re-run this execution'
                                    >
                                      {isRerunning ? (
                                        <Loader2 className='h-4 w-4 animate-spin' />
                                      ) : (
                                        <RotateCw className='h-4 w-4' />
                                      )}
                                    </Button>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}

          {/* Load more button */}
          {filteredExecutions.length > visibleExecutions && (
            <div className='flex justify-center'>
              <Button variant='outline' onClick={() => setVisibleExecutions(prev => prev + 50)}>
                Load More ({filteredExecutions.length - visibleExecutions} remaining)
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Re-run Confirmation Dialog */}
      <Dialog
        open={rerunConfirmDialogOpen}
        onOpenChange={open => {
          setRerunConfirmDialogOpen(open);
          if (!open) {
            setExecutionToRerun(null);
          }
        }}
      >
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Confirm Re-run Execution</DialogTitle>
            <DialogDescription>
              Review the execution details and input parameters before re-running. This will create
              a new execution with the same parameters.
            </DialogDescription>
          </DialogHeader>
          {executionToRerun && (
            <div className='space-y-4 py-4'>
              {/* Execution Summary */}
              <div className='space-y-2'>
                <Label className='text-base font-semibold'>Execution Summary</Label>
                <div className='space-y-2 p-3 bg-muted/50 rounded-lg border'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>Workflow Step:</span>
                    <Badge
                      variant={
                        executionToRerun.executionType === 'data_collection'
                          ? 'default'
                          : 'secondary'
                      }
                    >
                      {executionToRerun.executionType === 'data_collection'
                        ? 'Step 1: Refresh Car Inventory'
                        : 'Step 2: Process VINs'}
                    </Badge>
                  </div>
                  {executionToRerun.task_name && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Task:</span>
                      <span className='text-sm text-muted-foreground'>
                        {executionToRerun.task_name}
                      </span>
                    </div>
                  )}
                  {executionToRerun.run_id && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Run ID:</span>
                      <span className='text-sm font-mono text-muted-foreground'>
                        {executionToRerun.run_id}
                      </span>
                    </div>
                  )}
                  {executionToRerun.job_id && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Job ID:</span>
                      <span className='text-sm font-mono text-muted-foreground'>
                        {executionToRerun.job_id}
                      </span>
                    </div>
                  )}
                  {executionToRerun.started_at && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Started:</span>
                      <span className='text-sm text-muted-foreground'>
                        {new Date(executionToRerun.started_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Input Parameters */}
              <div className='space-y-2'>
                <Label className='text-base font-semibold'>Input Parameters</Label>
                <div className='p-3 bg-muted/50 rounded-lg border max-h-[300px] overflow-y-auto'>
                  {(() => {
                    const inputParams = executionToRerun['input_params'] || {};
                    const hasParams = Object.keys(inputParams).length > 0;

                    if (!hasParams && executionToRerun.executionType === 'data_collection') {
                      return (
                        <div className='space-y-2'>
                          {executionToRerun.task_name && (
                            <div>
                              <span className='text-sm font-medium'>Task Name: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.task_name}
                              </span>
                            </div>
                          )}
                          {executionToRerun.oem && (
                            <div>
                              <span className='text-sm font-medium'>OEM: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.oem}
                              </span>
                            </div>
                          )}
                          {executionToRerun.zone && (
                            <div>
                              <span className='text-sm font-medium'>Zone: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.zone}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (!hasParams) {
                      return (
                        <span className='text-sm text-muted-foreground'>
                          No input parameters available
                        </span>
                      );
                    }
                    return (
                      <div className='space-y-2'>
                        {Object.entries(inputParams).map(([key, value]) => {
                          let displayValue: string;
                          if (typeof value === 'object' && value !== null) {
                            displayValue = JSON.stringify(value, null, 2);
                          } else {
                            displayValue = String(value);
                          }
                          return (
                            <div
                              key={key}
                              className='border-b border-border/50 pb-2 last:border-0 last:pb-0'
                            >
                              <div className='text-sm font-medium mb-1'>{key}:</div>
                              <div className='text-sm text-muted-foreground font-mono bg-background p-2 rounded break-words'>
                                {displayValue}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Entry Point (for VIN Processing) */}
              {executionToRerun.executionType === 'vin_processing' &&
                executionToRerun.entry_point && (
                  <div className='space-y-2'>
                    <Label className='text-base font-semibold'>Entry Point</Label>
                    <div className='p-3 bg-muted/50 rounded-lg border'>
                      <span className='text-sm text-muted-foreground'>
                        {executionToRerun.entry_point === 'inventory_date_make'
                          ? 'Inventory Date + Make'
                          : executionToRerun.entry_point === 'step1_execution'
                            ? 'Step 1 Execution'
                            : executionToRerun.entry_point === 'apify_run_id'
                              ? 'Apify Run ID'
                              : executionToRerun.entry_point}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setRerunConfirmDialogOpen(false);
                setExecutionToRerun(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRerunExecution}
              disabled={runTaskMutation.isPending || createVINJobMutation.isPending}
            >
              {runTaskMutation.isPending || createVINJobMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Re-running...
                </>
              ) : (
                <>
                  <RotateCw className='h-4 w-4 mr-2' />
                  Confirm Re-run
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutionHistoryTab;
